'use client';

import { forwardRef, useEffect, useRef, useState } from 'react';

import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

const textAreaVariants = tv({
  base: 'flex transition-all slide-in-from-top-0 h-12 justify-center items-center resize-none overflow-hidden rounded w-full outline-none',
  variants: {
    variant: {
      default: 'text-base text-gray-900 font-normal',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  VariantProps<typeof textAreaVariants> & {
    onValueChange?: (value: string) => void;
    renderDelay?: number;
    forceTextAreaResize?: number;
  };

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, variant, defaultValue, ...props }, forwardedRef) => {
    const [text, setText] = useState(defaultValue);
    const localRef = useRef<HTMLTextAreaElement>(null);
    const textarea = localRef.current;

    const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      setText(value);
      props.onValueChange?.(value);
      event.target.innerText = value;
      event.target.textContent = value;
      event.target.style.height = '48px';
      event.target.style.height = `${event.target.scrollHeight}px`;
    };

    useEffect(() => {
      const resizeTextArea = () => {
        if (localRef.current) {
          localRef.current.style.height = '48px'; // Reset height to auto to recalculate correctly
          localRef.current.style.height = `${localRef.current.scrollHeight}px`;
        }
      };

      if (localRef.current) {
        // Listen for the load event to ensure content is loaded before resizing
        localRef.current.addEventListener('load', resizeTextArea);

        // Cleanup listener on unmount
        return () => {
          localRef.current?.removeEventListener('load', resizeTextArea);
        };
      }
    }, []);

    useEffect(() => {
      const resizeTextArea = () => {
        if (localRef.current) {
          localRef.current.style.height = 'auto';
          localRef.current.style.height = `${localRef.current.scrollHeight}px`;
        }
      };

      if (localRef.current) {
        resizeTextArea();
      }
    }, [props.forceTextAreaResize]);

    useEffect(() => {
      setText(defaultValue);
      setTimeout(() => {
        if (textarea) {
          localRef.current.innerText = defaultValue as string;
          localRef.current.textContent = defaultValue as string;
          localRef.current.style.height = `${localRef.current.scrollHeight}px`;
        }
      }, props.renderDelay || 100);
    }, [defaultValue]);

    useEffect(() => {
      // This allows the text area to be updated when the dictation value changes
      if (textarea) {
        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);

        textarea.addEventListener('input', () => {
          props.onValueChange?.(textarea.value);
        });

        textarea.removeEventListener('input', () => {
          props.onValueChange?.(textarea.value);
        });
      }
    }, [text]);

    const resizeTextArea = () => {
      if (localRef.current) {
        localRef.current.innerText = defaultValue as string;
        localRef.current.textContent = defaultValue as string;
        localRef.current.style.height = `${localRef.current.scrollHeight}px`;
      }
    };

    useEffect(() => {
      setTimeout(() => {
        resizeTextArea();
      }, props.renderDelay || 0);
    }, []);

    return (
      <textarea
        value={text}
        className={cn(
          textAreaVariants({
            variant,
            className,
          }),
        )}
        onChange={(e) => {
          handleInput(e);
        }}
        ref={(element) => {
          // @ts-expect-error - React ref types are not properly typed
          localRef.current = element;
          if (forwardedRef) {
            if (typeof forwardedRef === 'function') {
              forwardedRef(element);
            } else {
              forwardedRef.current = element;
            }
          }
        }}
        {...props}
      />
    );
  },
);

TextArea.displayName = 'TextArea';
